import { supabase } from '@/integrations/supabase/client';
import type {
  EventDeliverable,
  CreateEventDeliverableDto,
  UpdateEventDeliverableDto,
} from '../types/deliverable.types';
import { mapDbRowToEventDeliverable } from '../services/deliverable.mappers';

export class EventDeliverableRepository {
  /**
   * Fetch event deliverables for an event.
   */
  async getEventDeliverables(
    orgId: string,
    eventId: string
  ): Promise<EventDeliverable[]> {
    if (!eventId) return [];

    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data, error } = await supabase
      .from('event_deliverables')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapDbRowToEventDeliverable);
  }

  /**
   * Create an event deliverable with tenant organization association.
   */
  async createEventDeliverable(
    orgId: string,
    data: CreateEventDeliverableDto
  ): Promise<EventDeliverable> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data: result, error } = await supabase
      .from('event_deliverables')
      .insert([
        {
          ...data,
          organization_id: orgId,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return mapDbRowToEventDeliverable(result);
  }

  /**
   * Update an event deliverable.
   */
  async updateEventDeliverable(
    orgId: string,
    id: string,
    data: UpdateEventDeliverableDto
  ): Promise<EventDeliverable> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data: result, error } = await supabase
      .from('event_deliverables')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapDbRowToEventDeliverable(result);
  }

  /**
   * Delete an event deliverable.
   */
  async deleteEventDeliverable(
    orgId: string,
    id: string
  ): Promise<void> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { error } = await supabase
      .from('event_deliverables')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}

export const eventDeliverableRepository = new EventDeliverableRepository();
